<template>
    <div>
        <rkm-area
            v-if="paciente"
            no-margin-top
            title="Identificação do paciente"
        >
            <rkm-row>
                <rkm-col>
                    <h4>
                        {{ paciente.nome }}
                        <strong v-if="paciente.nomeSocial && paciente.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            ({{ paciente.nomeSocial }})
                        </strong>

                        <span
                            v-if="paciente.responsavelFamiliar"
                            class="ml-1"
                            title="Responsável familiar"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="crown" />
                        </span>

                        <span
                            v-if="isTea"
                            class="ml-1"
                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="infinity" />
                        </span>

                        <span
                            v-if="isPcd"
                            class="ml-1"
                            title="Pessoa com Deficiência - PCD"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="wheelchair" />
                        </span>
                    </h4>
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="paciente.acompanhamentosSaude && paciente.acompanhamentosSaude.length > 0">
                <rkm-col>
                    <div class="health-flags">
                        <span class="health-title">
                            Acompanhamentos:
                        </span>

                        <span
                            v-for="(acompanhamento, index) in paciente.acompanhamentosSaude"
                            :key="index"
                            :class="`health-badge acompanhamento-${acompanhamento.value}`"
                        >
                            {{ acompanhamento.text }}
                        </span>
                    </div>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <p>
                        <strong>Prontuário:</strong>
                        {{ paciente.prontuario }}
                    </p>
                    <p>
                        <strong>CPF:</strong>
                        {{ paciente.cpf }}
                    </p>
                    <p>
                        <strong>CNS:</strong>
                        {{ paciente.cns }}
                    </p>
                    <p>
                        <strong>RG:</strong>
                        {{ paciente.rg || '-' }}
                    </p>
                    <p>
                        <strong>Telefone:</strong>
                        <template v-if="paciente.telefoneNumero">
                            {{ paciente.telefoneNumero | formatPhoneOrCellPhone }}
                        </template>
                        <template v-else>
                            -
                        </template>
                    </p>
                </rkm-col>

                <rkm-col cols="4">
                    <p>
                        <strong>Data de nascimento:</strong>
                        {{ paciente.dataNascimento }}
                    </p>
                    <p>
                        <strong>Idade:</strong>
                        {{ paciente.idade }}
                    </p>
                    <p>
                        <strong>Sexo:</strong>
                        {{ paciente.sexo || '-' }}
                    </p>
                    <p>
                        <strong>Nome da mãe:</strong>
                        <template v-if="paciente.nomeMae">
                            {{ paciente.nomeMae }}
                        </template>
                        <template v-else>
                            <i>Desconhecido</i>
                        </template>
                    </p>
                </rkm-col>

                <rkm-col cols="4">
                    <p>
                        <strong>Classificação:</strong>
                        {{ classificacaoDescricao }}
                    </p>
                    <p>
                        <strong>Unidade de referência:</strong>
                        <template v-if="paciente.unidadeReferencia">
                            {{ paciente.unidadeReferencia }}
                        </template>
                        <template v-else>
                            <i>Não informado no cadastro do paciente</i>
                        </template>
                    </p>
                    <p>
                        <strong>Área e Microárea:</strong>
                        {{ paciente.area || 'Não Informada' }} / {{ paciente.microarea || 'Não Informada' }}
                    </p>
                    <p>
                        <strong>INE:</strong>
                        {{ paciente.ine || 'Não Informado' }}
                    </p>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <p>
                        <strong>Endereço:</strong>
                        {{ paciente.endereco || 'Não informado' }}
                    </p>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-table
            :fields="fields"
            :items="form.items"
            ref="Table"
            sort-by="dataAtendimento"
        >

            <template v-slot:cell(cns)="{ item }">
                {{ item.cns | formatCns }}
            </template>

            <template v-slot:cell(dataExportacao)="{ item }">

                <span v-if="item.dataExportacao">
                    {{ item.dataExportacao }} às {{ item.horarioExportacao }} - {{ item.profissionalExportacao }}
                </span>
                <span v-else>-</span>
            </template>

            <template v-slot:cell(actions)="row">
                <rkm-button
                    @click="$router.push({
                        name: 'atencao-basica-marcador-consumo-alimentar-edit',
                        params: {
                            id: row.item.id
                        }
                    })"
                    color="primary"
                    title="Editar"
                    small
                >
                    <rkm-icon icon="pen" />
                </rkm-button>

                <rkm-button
                    @click.stop="handleDestroy(row.item.id)"
                    color="danger"
                    title="Excluir"
                    small
                >
                    <rkm-icon icon="trash" />
                </rkm-button>
            </template>
        </rkm-table>
    </div>
</template>

<script>

// Mixins
import { error, repository } from '@/mixins'

// Vuex
import { mapActions } from 'vuex'

// Filters.
import formats from '@/filters/formats'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

export default {
    data() {
        return {
            form: {},
            paciente: null,
            enums: {preferenciaNome, classificacao, subclassificacao, deficienciaBase, deficiencia},
            fields: [
                {
                    key: 'dataAtendimento',
                    label: 'Data',
                    sortable: true,
                },
                {
                    key: 'nome',
                    label: 'Profissional',
                    sortable: true
                },
                {
                    key: 'cns',
                    label: 'CNS',
                    sortable: true
                },
                {
                    key: 'nomeFantasia',
                    label: 'Unidade',
                    searchable: true,
                    sortable: true
                },
                {
                    key: 'dataExportacao',
                    label: 'Exportação',
                },
                {
                    name: 'actions',
                    key: 'actions',
                    label: 'Ações',
                    class: 'col-min'
                },
            ]
        }
    },
    computed: {
        classificacaoDescricao() {
            const paciente = this.paciente

            if (paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {
                return 'Temporário'
            }

            if (paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Outro Município'
            }

            return 'Munícipe'
        },
        isTea() {
            const ids = this.paciente.deficienciasBaseIds || []

            return ids.includes(this.enums.deficienciaBase.TEA)
                || this.paciente.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {
            const ids = this.paciente.deficienciasBaseIds || []

            return ids.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.paciente.deficienciaId && this.paciente.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
    },
    created() {
        this.repositories = {
            marcadorConsumoAlimentar: this.getConectaSusRepository('atencaoBasica.marcadorConsumoAlimentar')
        }
    },
    mounted() {
        this.load()
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            if (this.id) {

                const request = this.repositories.marcadorConsumoAlimentar.historico(this.id)

                request
                    .then(response => {
                        this.form = {items: response.data.form.map(item => Object.assign({}, item))}
                        this.paciente = response.data.paciente
                        this.$emit('paciente', response.data.paciente)
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        handleDestroy(id) {
            const destroy = () => this.repositories.marcadorConsumoAlimentar.destroy(id)
                .then(() => this.form.items.splice(this.form.items.findIndex(i => i.id === id), 1))

            this.showDeleteConfirmation('Deseja realmente excluir o marcador?', destroy, this.handleError)
        }
    },
    filters: {
        ...formats
    },
    mixins: [ error, repository ],
    props: { id: { type: Number, default: null } }
}

</script>

<style scoped>
p {
    margin: 0px;
}

.health-flags {
    margin-top: 9px;
    padding-top: 2px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e5e7eb;
}

.health-title {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.health-badge {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
}

.acompanhamento-idoso {
    color: #155724;
}

.acompanhamento-gestante {
    color: #ffb6c1;
}

.acompanhamento-hipertenso,
.acompanhamento-diabetico {
    color: #856404;
}

.acompanhamento-saude-mulher {
    color: #b34db2;
}

.acompanhamento-crescimento-desenvolvimento-infantil {
    color: #004085;
}
</style>